import { Component, OnInit, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { ICourse } from '@toro/common/shared/interfaces/infoproducts/course.interfaces';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import { InfoproductsAction } from '@toro/common/shared/state-management/infoproducts/infoproducts.action';
import { Observable, Subscription } from 'rxjs';
import 'slick-carousel';
import { CampaignNameEnum } from '../../../../shared/enums/campaign-name.enum';
import { IPlatform } from '@toro/common/shared/models/advanced-platforms/iplatform-model';
import { FinancePosition } from '@toro/common/shared/models/pos-risk/finance-position';
import { PlatformsIds } from '@toro/common/shared/enum/advanced-platforms/platforms-ids.enum';
import { SubscriptionStatus } from '@toro/common/shared/enum/advanced-platforms/subscription-status.enum';
import { IPlatformsState } from '@toro/common/shared/state-management/advanced-platforms/advanced-platforms.reducer';
import { filter, skip } from 'rxjs/operators';
import { PendingSubscriptionStatus } from '@toro/common/shared/enum/advanced-platforms/pending-subscription-status.enum';
import { AdvancedPlatformsActions } from '@toro/common/shared/state-management/advanced-platforms/advanced-platforms.action';
import { StorageService } from '@toro/common/shared/services/storage.service';
import { Router } from '@angular/router';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';

@Component({
  selector: 'day-trade-course-card',
  templateUrl: './day-trade-course-card.component.html',
  styleUrls: ['./day-trade-course-card.component.scss']
})
export class DayTradeCourseCardComponent implements OnInit {
  @Input() openAccount: boolean = false;
  public coursesSubscription: Subscription;
  public campaignName: string;
  public userPending: boolean = true;
  public dayTradeCourse: null | ICourse = null;
  public toroTraderData: IPlatform = null;
  private platfomData: IPlatform[];
  public subscriptionStatus = SubscriptionStatus;
  public platforms$: Observable<IPlatformsState>;
  public platformsList: IPlatform[] = [];
  private disposableSubscriptions: Subscription[] = [];
  public shouldRenderCard = false;

  private courseIds = {
    prd: 'e9fad66d-f5f3-45ab-94d2-c0e5c94b224f',
    hml: 'e61c82d4-4580-41c2-9596-ab35025e57b4'
  };

  private currentCourseId = this.courseIds[
    this.settingsService.getEnvironment() === 'production' ? 'prd' : 'hml'
  ];

  get isFromDTLpCampaign() {
    return this.campaignName === CampaignNameEnum.CURSO_DT_INICIANTES;
  }

  public getUserProfile() {
    this.store
      .select(state => state.userProfile.user)
      .filter((user: any) => user && !user.pending)
      .subscribe(user => {
        if (user.campaignName) {
          this.campaignName = user.campaignName;
        }
        this.userPending = false;
      });
  }

  constructor(
    private store: Store<AppState>,
    private settingsService: SettingsService,
    public router: Router
  ) {
    this.checkCardTTL();
    this.store.dispatch({ type: InfoproductsAction.getAvailableCourses });
    this.store.dispatch({ type: AdvancedPlatformsActions.getPlatforms });
    this.store.dispatch({ type: AdvancedPlatformsActions.getSubscribes });

    this.initializeStoreSubscriptions();
  }

  public hasStartedCourse() {
    return this.dayTradeCourse && this.dayTradeCourse.progress.percent > 0;
  }

  get nextClassTitle() {
    if (!this.dayTradeCourse || !this.dayTradeCourse.nextLesson) {
      return null;
    }
    const { nextLesson, modules } = this.dayTradeCourse;

    return modules[nextLesson.moduleId].lessons[nextLesson.lessonId].name;
  }

  private trackEvent(element: string, group: string) {
    const { nextLesson, modules } = this.dayTradeCourse;
    this.sendTracking('Element Clicked', {
      'Product Area Origin': 'Home Trader',
      Element: element,
      Group: group,
      Step: `${this.dayTradeCourse.modules[nextLesson.moduleId].name}/${modules[nextLesson.moduleId].lessons[nextLesson.lessonId].name}`,
      Text: this.dayTradeCourse.name,
      'Time Responsável': 'Sky'
    });
  }

  public openTransferResources() {
    this.trackEvent('Ative sua Conta', 'Card de Progresso - Curso Home Trader');
    this.router.navigate(['/home/transferencia']);
  }

  public openPlatforms() {
    const group = 'Card de Progresso - Curso Home Trader';

    this.trackEvent('Ative a Plataforma', group);

    window.location.href = '/minha-conta/plataformas';
  }

  public redirectToCourse() {
    const element = this.hasStartedCourse()
      ? ' Continue aprendendo'
      : 'Começar curso de Day Trade';
    const group = 'Card de Progresso - Curso Home Trader';

    this.trackEvent(element, group);
  }

  public ngOnInit(): void {
    this.coursesSubscription = this.store
      .select(({ infoproducts }) => infoproducts)
      .subscribe(({ availableCourses, customerCourses }) => {
        availableCourses.forEach(course => {
          if (course.id === this.currentCourseId) {
            this.dayTradeCourse = course;
          }
        });
      });

    this.updateSubscribeStatusFromPlatforms();
  }

  private initializeStoreSubscriptions() {
    this.platforms$ = this.store
      .select(state => state.advancedPlatforms)
      .pipe(filter(advancedPlatforms => !!advancedPlatforms));
    this.disposableSubscriptions.push(
      this.platforms$.subscribe(platformsData => {
        this.platformsList = platformsData.list;
        if (
          platformsData.pendingSubscriptionList &&
          platformsData.pendingSubscriptionList.length > 0
        ) {
          platformsData.pendingSubscriptionList.forEach(pendingSubscription => {
            if (
              pendingSubscription.processStatus ===
                PendingSubscriptionStatus.AWAITING ||
              pendingSubscription.processStatus ===
                PendingSubscriptionStatus.ERROR
            ) {
              this.platformsList.find(
                item => item.id === pendingSubscription.platformId
              ).isPendingSubscription = true;
            }
          });
        }
      })
    );
  }

  private updateSubscribeStatusFromPlatforms() {
    this.disposableSubscriptions.push(
      this.platforms$
        .combineLatest(this.store.select(({ finance }) => finance))
        .pipe(
          skip(1),
          filter(
            ([{ list }, { finance }]) =>
              list.length > 0 && typeof finance.riskType === 'number'
          )
        )
        .subscribe(([{ list }, { finance }]) => {
          this.updatePlatformsWithStatusBlocked(list, finance);
        })
    );
  }

  private updatePlatformsWithStatusBlocked(
    list: IPlatform[],
    finance: FinancePosition = null
  ) {
    this.platfomData = list.map(platform => ({
      ...platform,
      isBlockedBuyPlatform:
        platform.subscriptionStatus === this.subscriptionStatus.Canceled &&
        finance.availableOnAccountD0 < 0
    }));
    this.toroTraderData = this.platfomData.find(
      plt => plt.id === PlatformsIds.ToroTraderProfit
    );
  }

  public sendTracking(event: string, breakdowns: any) {
    this.store.dispatch({
      type: TrackingActions.trackEvents,
      payload: { type: event, events: breakdowns }
    });
  }

  private checkCardTTL() {
    const cookieName = 'DT_CARD_COURSE';
    const currentTimestamp = new Date().getTime();
    const value = { timestamp: currentTimestamp };
    const expirationTime = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

    const cookie = StorageService.getCookie(cookieName);

    if (cookie) {
      const data = JSON.parse(cookie);
      const currentTimestamp = new Date().getTime();

      if (currentTimestamp - data.timestamp > expirationTime) {
        this.shouldRenderCard = false;
        return;
      }

      this.shouldRenderCard = true;

      return;
    }

    StorageService.setCookie(
      cookieName,
      JSON.stringify(value),
      new Date(Date.now() + expirationTime)
    ); // Expires in 7 days
    this.shouldRenderCard = true;
  }
}
